import { inject } from "vue";

export function useEventValidation() {
  const swal = inject("$swal");

  const validateDates = (formData, today) => {
    const nowDate = new Date(today);
    const startEvent = new Date(formData.init_date);
    const endEvent = new Date(formData.end_date);
    const startIns = new Date(formData.init_date_inscription);
    const endIns = new Date(formData.end_date_inscription);

    if (
      startEvent < nowDate ||
      endEvent < nowDate ||
      startIns < nowDate ||
      endIns < nowDate
    ) {
      return {
        valid: false,
        title: "Fechas inválidas",
        message: "No se permite ingresar fechas menores a la fecha actual.",
      };
    }

    if (endEvent < startEvent) {
      return {
        valid: false,
        title: "Fechas inválidas",
        message:
          "La fecha de fin del evento no puede ser menor que la fecha de inicio.",
      };
    }

    if (endIns < startIns) {
      return {
        valid: false,
        title: "Fechas inválidas",
        message:
          "La fecha de fin de inscripción no puede ser menor que la fecha de inicio.",
      };
    }

    if (startIns > startEvent) {
      return {
        valid: false,
        title: "Fechas inválidas",
        message:
          "La fecha de inicio de inscripciones no puede ser posterior a la fecha de inicio del evento.",
      };
    }

    if (endIns > endEvent) {
      return {
        valid: false,
        title: "Fechas inválidas",
        message:
          "La fecha de fin de inscripciones no puede ser posterior a la fecha de fin del evento.",
      };
    }

    return { valid: true };
  };

  const validateCapacity = (
    formData,
    placeCapacity,
    withLimitControl,
    isVirtual
  ) => {
    if (isVirtual) return { valid: true };

    if (parseInt(formData.minimum) === 0) {
      return {
        valid: false,
        title: "Cantidad inválida",
        message: "La cantidad mínima no puede ser 0.",
      };
    }

    if (parseInt(formData.maximum) < parseInt(formData.minimum)) {
      return {
        valid: false,
        title: "Cantidad inválida",
        message: "La cantidad máxima no puede ser menor que la mínima.",
      };
    }

    if (withLimitControl && formData.place_code) {
      if (parseInt(formData.maximum) > placeCapacity) {
        return {
          valid: false,
          title: "Capacidad excedida",
          message: `La cantidad máxima (${formData.maximum}) no puede ser mayor a la capacidad del lugar seleccionado (${placeCapacity}). Desactive el "Control de capacidad" si desea omitir esta validación.`,
        };
      }
    }

    return { valid: true };
  };

  const validateEventType = (kindName, formData) => {
    const isVirtual = kindName === "virtual";
    const isPresencial = kindName === "presencial";
    const isHybrid = kindName.includes("presencial-virtual");

    if (isPresencial) {
      if (!formData.place_code) {
        return {
          valid: false,
          title: "Lugar requerido",
          message: "Para eventos presenciales debe seleccionar un lugar.",
        };
      }

      if (!formData.minimum || !formData.maximum) {
        return {
          valid: false,
          title: "Capacidad requerida",
          message:
            "Para eventos presenciales debe especificar cantidad mínima y máxima.",
        };
      }
    }

    if (isHybrid) {
      if (formData.place_code && (!formData.minimum || !formData.maximum)) {
        return {
          valid: false,
          title: "Capacidad requerida",
          message:
            "Si selecciona un lugar para el evento híbrido, debe especificar cantidad mínima y máxima.",
        };
      }
    }

    return { valid: true };
  };

  const validateSchedules = (schedulesJson) => {
    if (!schedulesJson) {
      return {
        valid: false,
        title: "Horarios requeridos",
        message: "Debe ingresar al menos un horario para el evento.",
      };
    }

    try {
      const schedulesObj = JSON.parse(schedulesJson);
      const dias = Object.values(schedulesObj);
      const diasInvalidos = dias.some(
        (dia) => !dia.horarios.some((h) => h.inicio && h.fin)
      );

      if (diasInvalidos) {
        return {
          valid: false,
          title: "Horarios incompletos",
          message:
            "Debe ingresar al menos un horario (inicio y fin / o solamente inicio) por cada día.",
        };
      }
    } catch (error) {
      return {
        valid: false,
        title: "Error en horarios",
        message:
          "Hubo un error al procesar los horarios. Por favor, verifique los datos.",
      };
    }

    return { valid: true };
  };

  const validateComponent = async (componentRef, methodName, errorConfig) => {
    if (!componentRef.value) {
      console.warn(`Componente es null: ${errorConfig.title}`);
      return false;
    }

    if (typeof componentRef.value[methodName] !== "function") {
      console.warn(
        `${methodName} no encontrado en componente: ${errorConfig.title}`
      );
      return false;
    }

    const isValid = await componentRef.value[methodName]();

    if (isValid === false && errorConfig) {
      swal.fire({
        title: errorConfig.title,
        text: errorConfig.message,
        icon: "error",
      });
    }

    return isValid;
  };

  const showValidationError = (validation) => {
    if (!validation.valid) {
      swal.fire({
        title: validation.title,
        text: validation.message,
        icon: "error",
      });
      return true;
    }
    return false;
  };

  const validateAll = async ({
    formData,
    today,
    kinds,
    scheduleComponent,
    multiOrientedSelector,
    enrollmentSelector,
    withLimitControl,
    getPlaceCapacity,
  }) => {
    const currentKind = kinds.find((k) => k.id === formData.kind_event_code);
    const kindName = currentKind?.name?.toLowerCase() || "";

    const typeValidation = validateEventType(kindName, formData);
    if (showValidationError(typeValidation)) return false;

    const orientedValid = await validateComponent(
      multiOrientedSelector,
      "validate",
      {
        title: "Orientaciones requeridas",
        message: "Debe seleccionar al menos una orientación para el evento.",
      }
    );
    if (!orientedValid) return false;

    const enrollmentValid = await validateComponent(
      enrollmentSelector,
      "validate",
      {
        title: "Formulario de inscripción requerido",
        message:
          "Debe seleccionar un formulario de inscripción para el evento.",
      }
    );
    if (!enrollmentValid) return false;

    const isVirtual = kindName === "virtual";
    if (!isVirtual) {
      const placeCapacity = formData.place_code
        ? getPlaceCapacity(formData.place_code)
        : 0;
      const capacityValidation = validateCapacity(
        formData,
        placeCapacity,
        withLimitControl,
        isVirtual
      );
      if (showValidationError(capacityValidation)) return false;
    }

    const dateValidation = validateDates(formData, today);
    if (showValidationError(dateValidation)) return false;

    if (scheduleComponent.value) {
      const hasErrors = await scheduleComponent.value.hasValidationErrors?.();
      if (hasErrors) {
        swal.fire({
          title: "Horarios inválidos",
          text: "Por favor, corrija los horarios que están fuera del rango permitido.",
          icon: "error",
        });
        return false;
      }

      const schedulesJson = scheduleComponent.value.getScheduleJson?.();
      const scheduleValidation = validateSchedules(schedulesJson);
      if (showValidationError(scheduleValidation)) return false;
    }

    return true;
  };

  return {
    validateDates,
    validateCapacity,
    validateEventType,
    validateSchedules,
    validateComponent,
    validateAll,
    showValidationError,
  };
}
